import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaLock, FaArrowLeft, FaCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authAPI } from '../../api/endpoints';
import LoadingSpinner from '../common/LoadingSpinner';
import './Auth.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const login = params.get('login') || '';

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const newPassword = watch('password', '');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await authAPI.resetPassword({
        key,
        login,
        password: data.password,
      });
      const payload = res?.data || {};

      const isSuccess =
        payload.success === true ||
        payload.data?.success === true ||
        payload.code === 'password_reset';

      if (isSuccess) {
        setSubmitted(true);
        toast.success('Password updated! You can now log in.');
      } else {
        toast.error(
          payload.message ||
          payload.data?.message ||
          'Could not reset password.'
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.data?.message ||
        'Failed to reset password. The link may have expired.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Missing params → tell the user the link is broken
  if (!key || !login) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Invalid Reset Link</h1>
              <p className="auth-subtitle">
                This password reset link is missing required information.
                Please request a new one.
              </p>
            </div>
            <Link to="/forgot-password" className="auth-button">
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card success-card">
            <div className="success-icon">
              <FaCheck />
            </div>
            <h2>Password Updated</h2>
            <p>Your password has been changed successfully.</p>
            <div className="success-actions">
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary"
                type="button"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/login" className="back-to-login">
              <FaArrowLeft /> Back to Login
            </Link>
            <div className="auth-logo">
              <span className="logo-icon">🔑</span>
              <h1>Reset Password</h1>
            </div>
            <p className="auth-subtitle">
              Enter a new password for your account.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <div className="form-group">
              <label>New Password</label>
              <div className="input-wrapper">
                <FaLock className="input-icon" />
                <input
                  type="password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                />
              </div>
              {errors.password && (
                <span className="error-message">{errors.password.message}</span>
              )}
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="input-wrapper">
                <FaLock className="input-icon" />
                <input
                  type="password"
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  {...register('confirm', {
                    required: 'Please confirm your password',
                    validate: (v) =>
                      v === newPassword || 'Passwords do not match',
                  })}
                />
              </div>
              {errors.confirm && (
                <span className="error-message">{errors.confirm.message}</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;